'use client';

import type { ComponentProps, ReactNode } from 'react';

import { ChevronDown } from 'lucide-react';

import { Card } from '@kit/ui/card';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';
import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';
import { NativePageShell } from '~/(tenant)/_components/native/_primitives';
import type { PackagesDisplayMode } from '~/(tenant)/_lib/schema/native-home-config.schema';

import styles from './booking-presentation.module.css';

/** Explicitly applied to portal contents as well as the page surface. */
export function useBookingPresentation() {
  const presentation = useNativePresentation();
  return {
    ...presentation,
    className: presentation.className
      ? cn(presentation.className, 'native-booking-surface', styles.theme)
      : undefined,
  };
}

export function BookingPageSurface({
  children,
  dir,
}: {
  children: ReactNode;
  dir?: 'rtl' | 'ltr';
}) {
  const isNativeLayout = useNativeLayout();
  const presentation = useBookingPresentation();

  if (!isNativeLayout) {
    return (
      <div
        className={cn(
          'mx-auto w-full max-w-[120rem] px-4 py-8 sm:px-6 lg:px-8',
          styles.webPage,
        )}
        dir={dir}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      {...presentation}
      className={cn(presentation.className, styles.page)}
      dir={dir}
      data-test="native-book-page"
    >
      {/* No page title: the booking sections start right under the header. */}
      <NativePageShell header={null} paddingTop={8}>
        {children}
      </NativePageShell>
    </div>
  );
}

export function BookingSection({
  className,
  ...props
}: ComponentProps<typeof Card>) {
  return (
    <Card
      {...props}
      data-native-booking-section
      className={cn(styles.section, className)}
    />
  );
}

export function BookingPackageSuggestions({
  children,
  mode = 'collapsed',
}: {
  children: ReactNode;
  /** Tenant-configured display mode. See `bookingConfigSchema`. */
  mode?: PackagesDisplayMode;
}) {
  const isNativeLayout = useNativeLayout();
  if (!isNativeLayout) return children;
  if (mode === 'hidden') return null;

  // Expanded: no disclosure row at all, just the list under the same single
  // hairline every other native booking section uses.
  if (mode === 'expanded') {
    return <div className={styles.packages}>{children}</div>;
  }

  // Only a top border: the following BookingSection draws its own border-top,
  // so a border-bottom here would stack two hairlines with a gap between them.
  return (
    <details className={cn('group/packages py-3', styles.packages)}>
      <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium">
        <span className="group-open/packages:hidden">
          <Trans
            i18nKey="common:viewAvailablePackages"
            defaults="View available packages"
          />
        </span>
        <span className="hidden group-open/packages:inline">
          <Trans i18nKey="common:hidePackages" defaults="Hide packages" />
        </span>
        <ChevronDown
          aria-hidden="true"
          className="ms-auto h-4 w-4 shrink-0 group-open/packages:rotate-180"
        />
      </summary>
      <div className="pt-3">{children}</div>
    </details>
  );
}
